import { useState } from 'react';
import { UniqueIdentifier } from '@dnd-kit/core';
import {
  Control,
  Controller,
  FieldPathByValue,
  FieldValues,
  get,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Tile, TileDraggable } from '@rossum/components/tile';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import {
  DeclaredColumnType,
  declaredColumnTypes,
  ElisColumnType,
  formatBsonType,
  isDeclaredColumnType,
} from '../types';
import { dataTypes } from './dataTypes';

type ColumnTileProps<T extends FieldValues> = {
  control: Control<T>;
  // The column name, which can be changed on every column.
  labelField: FieldPathByValue<T, string>;
  attributeName: string;
  type: ElisColumnType;
  // The raw inferred type, shown when it maps to no known type.
  bsonType?: string | string[] | null;
  // A column declared in the form: its attribute name and type can change,
  // and it can be deleted and dragged. Without it the column is inferred from
  // the data and only its name can change.
  declared?: {
    attributeNameField: FieldPathByValue<T, string>;
    typeField: FieldPathByValue<T, DeclaredColumnType>;
    onLabelBlur: () => void;
    onDelete: () => void;
    dragId: UniqueIdentifier;
  };
  'data-cy'?: string;
};

const TypeIcon = ({ type }: { type: ElisColumnType }) => {
  const intl = useIntl();
  const Icon = dataTypes(intl)[type].icon;
  return (
    <SvgIcon fontSize="small" sx={{ color: 'text.secondary' }}>
      <Icon />
    </SvgIcon>
  );
};

// One dataset column: a header with its type, name and attribute name that
// expands to its fields.
export const ColumnTile = <T extends FieldValues>({
  control,
  labelField,
  attributeName,
  type,
  bsonType,
  declared,
  'data-cy': dataCy,
}: ColumnTileProps<T>) => {
  const intl = useIntl();
  const types = dataTypes(intl);
  const label = useWatch({ control, name: labelField });
  // A declared column starts open, so a new one shows its inputs and gets
  // focus.
  const [expanded, setExpanded] = useState(declared !== undefined);

  // Reopen the column once it has an error, so the error can be seen and
  // fixed; adjusted while rendering rather than in an effect.
  const fieldNames = declared
    ? [labelField, declared.attributeNameField, declared.typeField]
    : [labelField];
  // Only this column's errors, so validating other fields doesn't re-render it.
  const { errors } = useFormState({ control, name: fieldNames });
  const invalid = fieldNames.some(name => get(errors, name) !== undefined);
  if (invalid && !expanded) setExpanded(true);

  const newColumn = intl.formatMessage({
    id: 'features.datasets.settings.form.columnsSection.newColumn',
  });
  const typeLabel = types[type].label;
  const nameLabel = intl.formatMessage({
    id: 'features.datasets.settings.form.columnsSection.name.label',
  });
  const attributeNameLabel = intl.formatMessage({
    id: 'features.datasets.settings.form.columnsSection.datasetAttributeName.label',
  });
  const typeFieldLabel = intl.formatMessage({
    id: 'features.datasets.settings.form.columnsSection.type.label',
  });

  const tile = (
    <Tile
      iconProps={{ content: types[type].icon, color: 'secondary' }}
      title={typeof label === 'string' && label ? label : newColumn}
      description={
        attributeName ? `${typeLabel} · ${attributeName}` : typeLabel
      }
      quickActions={[]}
      expandable={{
        expanded,
        onToggleExpanded: () => setExpanded(prev => !prev),
        content: (
          <Stack p={3} alignItems="flex-end">
            <Stack spacing={3} width="50%">
              <Controller
                control={control}
                name={labelField}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      declared?.onLabelBlur();
                    }}
                    // On the input, so a new or invalid column gets focus.
                    inputRef={ref}
                    size="small"
                    label={nameLabel}
                    placeholder={newColumn}
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
              {declared ? (
                <Controller
                  control={control}
                  name={declared.attributeNameField}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      size="small"
                      label={attributeNameLabel}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              ) : (
                <TextField
                  value={attributeName}
                  size="small"
                  label={attributeNameLabel}
                  disabled
                  fullWidth
                />
              )}
              {declared ? (
                <Controller
                  control={control}
                  name={declared.typeField}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      select
                      size="small"
                      label={typeFieldLabel}
                      helperText={intl.formatMessage({
                        id: 'features.datasets.create.columns.type.helperText',
                      })}
                      SelectProps={{
                        renderValue: value =>
                          isDeclaredColumnType(value) ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <TypeIcon type={value} />
                              <span>{types[value].label}</span>
                            </Stack>
                          ) : null,
                      }}
                      fullWidth
                    >
                      {declaredColumnTypes.map(option => (
                        <MenuItem key={option} value={option}>
                          <ListItemIcon>
                            <TypeIcon type={option} />
                          </ListItemIcon>
                          <ListItemText>{types[option].label}</ListItemText>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              ) : (
                <TextField
                  value={typeLabel}
                  size="small"
                  label={typeFieldLabel}
                  // Surface the raw inferred bsonType when it fails to map to
                  // a known type (e.g. unions like `int | string`), as a
                  // debugging aid.
                  helperText={
                    type === 'unknown' ? formatBsonType(bsonType) : undefined
                  }
                  disabled
                  fullWidth
                />
              )}
              {declared ? (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    color="error"
                    variant="text"
                    startIcon={
                      <SvgIcon fontSize="small">
                        <IconTrash />
                      </SvgIcon>
                    }
                    onClick={declared.onDelete}
                    data-cy="dataset-column-delete"
                  >
                    {intl.formatMessage({
                      id: 'features.datasets.create.columns.remove',
                    })}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        ),
      }}
      data-cy={dataCy}
    />
  );

  return declared ? (
    <TileDraggable id={declared.dragId}>{tile}</TileDraggable>
  ) : (
    tile
  );
};
